import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useBrandContext } from '../../context/BrandContext';
import { useHeaderContext } from '../../context/HeaderContext';
import { useToast } from '../../context/ToastModalContext';

import Button from '../../components/button/Button';
import Input from '../../components/input/Input';

import { changePassword } from '../../services/userService';

import switchPasswordIcon from '../../assets/check_circle.svg';
import { validatePassword } from '../../utils/format';

import './SwitchPassword.css';
const SwitchPassword = () => {
  type Inputs = {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  };
  type Touched = Partial<Record<keyof Inputs, boolean>>;
  type Errors = Partial<Record<keyof Inputs, string>>;
  const [form, setForm] = useState<Inputs>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [touched, setTouched] = useState<Touched>({});
  const [errorsValidate, setErrorsValidate] = useState<Errors>({});
  const [errorFetch, setErrorFetch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { hasPassword } = useParams();

  const { brandBgColor, brandName } = useBrandContext();
  const { setHeaderConfig } = useHeaderContext();
  const hasPass = hasPassword === 'true';

  const validate = (newInputs: Inputs): Errors => {
    let newErrors: Errors = {};

    if (hasPass) {
      if (!form.currentPassword.trim()) {
        newErrors.currentPassword = 'Campo Obrigatório';
      } else if (!validatePassword(newInputs.currentPassword)) {
        newErrors.currentPassword =
          'A senha precisa ter pelo menos 6 caracteres. \nPor favor, tente novamente.';
      }
    }

    if (!form.newPassword.trim()) {
      newErrors.newPassword = 'Campo Obrigatório';
    } else if (!validatePassword(newInputs.newPassword)) {
      newErrors.newPassword =
        'A senha precisa ter pelo menos 6 caracteres. \nPor favor, tente novamente.';
    }

    if (!form.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Campo Obrigatório';
    } else if (!validatePassword(newInputs.confirmPassword)) {
      newErrors.confirmPassword =
        'A senha precisa ter pelo menos 6 caracteres. \nPor favor, tente novamente.';
    } else if (newInputs.confirmPassword !== newInputs.newPassword) {
      newErrors.confirmPassword =
        'A confirmação de senha deve ser igual à nova senha';
    }

    setErrorsValidate(newErrors);
    return newErrors;
  };

  const handleKeyPress = (event: any) => {
    if (event.key === ' ') {
      event.preventDefault();
    }
  };

  const handleShowCurrentPassword = () => {
    setShowCurrentPassword((showCurrentPassword) => !showCurrentPassword);
  };

  const handleShowNewPassword = () => {
    setShowNewPassword((showNewPassword) => !showNewPassword);
  };

  const handleShowConfirmNewPassword = () => {
    setShowConfirmPassword((showConfirmPassword) => !showConfirmPassword);
  };

  const handleSubmit = async (event: { preventDefault: () => void }) => {
    event.preventDefault();
    const errors = validate(form);
    if (Object.keys(errors).length === 0) {
      setIsLoading(true);
      const payload = !hasPass
        ? {
            newPassword: form.newPassword,
          }
        : {
            oldPassword: form.currentPassword,
            newPassword: form.newPassword,
          };
      try {
        const response = await changePassword(payload);
        if (response.status === 200) {
          showToast({
            messageModal: 'Sua senha foi alterada com sucesso',
            allowButtonClosed: true,
            closeButtonText: 'OK',
          });
          navigate(`/${brandName}/userProfile`);
        }

        setIsLoading(false);
        setErrorFetch('');
      } catch (error: any) {
        setIsLoading(false);
        console.log(error);
        if (error?.response?.data?.message === 'Error: Incorrect password.') {
          setErrorFetch('Senha atual incorreta. Por favor, tente novamente.');
        } else if (error?.message === 'Network Error') {
          setErrorFetch(
            'Sua conexão está instável. Por favor, tente mais tarde.'
          );
        } else {
          setErrorFetch('Falha no servidor. Por favor tente novamente.');
        }
      }
    }
  };

  useEffect(() => {
    setHeaderConfig({
      headerText: hasPass ? 'Altere sua senha' : 'Defina sua senha',
      backNavigation: () => navigate(`/${brandName}/userProfile`),
    });
    return () => setHeaderConfig({});
  }, [brandName, hasPass, navigate, setHeaderConfig]);

  return (
    <div className='switch-password-wrapper'>
      <div className='switch-password-container'>
        <form onSubmit={handleSubmit} className='switch-password-form'>
          <div className='switch-password-fields'>
            {hasPass && (
              <Input
                htmlFor='currentPassword'
                type={showCurrentPassword ? 'text' : 'password'}
                id='currentPassword'
                labelName='Senha atual'
                className={
                  errorsValidate.currentPassword
                    ? 'input-pattern error-input'
                    : 'input-pattern'
                }
                placeholder='Senha atual'
                value={form.currentPassword}
                onKeyDown={(event) => handleKeyPress(event)}
                onChange={(event) => {
                  setForm({ ...form, currentPassword: event.target.value });
                }}
                onBlur={() => setTouched({ ...touched, currentPassword: true })}
                isErrorValidate={errorsValidate.currentPassword ? true : false}
                isPassword={true}
                handleShowPassword={handleShowCurrentPassword}
                showPassword={showCurrentPassword}
                messageError={errorsValidate.currentPassword}
                style={{ flexGrow: 1, marginRight: '-19px' }}
              />
            )}
            <Input
              htmlFor='newPassword'
              type={showNewPassword ? 'text' : 'password'}
              id='newPassword'
              labelName='Nova senha'
              className={
                errorsValidate.newPassword
                  ? 'input-pattern error-input'
                  : 'input-pattern'
              }
              placeholder='Nova senha'
              value={form.newPassword}
              onKeyDown={(event) => handleKeyPress(event)}
              onChange={(event) => {
                setForm({ ...form, newPassword: event.target.value });
              }}
              onBlur={() => setTouched({ ...touched, newPassword: true })}
              isErrorValidate={errorsValidate.newPassword ? true : false}
              isPassword={true}
              handleShowPassword={handleShowNewPassword}
              showPassword={showNewPassword}
              messageError={errorsValidate.newPassword}
              style={{ flexGrow: 1, marginRight: '-19px' }}
            />

            <Input
              htmlFor='confirmPassword'
              type={showConfirmPassword ? 'text' : 'password'}
              id='confirmPassword'
              labelName='Confirme nova senha'
              className={
                errorsValidate.confirmPassword
                  ? 'input-pattern error-input'
                  : 'input-pattern'
              }
              placeholder='Confirme sua nova senha'
              value={form.confirmPassword}
              onKeyDown={(event) => handleKeyPress(event)}
              onChange={(event) => {
                setForm({ ...form, confirmPassword: event.target.value });
              }}
              onBlur={() => setTouched({ ...touched, confirmPassword: true })}
              isErrorValidate={errorsValidate.confirmPassword ? true : false}
              isPassword={true}
              handleShowPassword={handleShowConfirmNewPassword}
              showPassword={showConfirmPassword}
              messageError={errorsValidate.confirmPassword}
              style={{ flexGrow: 1, marginRight: '-19px' }}
            />

            <b className='error-fetch-switch-password'>
              {' '}
              {errorFetch ? errorFetch : null}
            </b>
            <Button
              style={{
                backgroundColor: brandBgColor ? brandBgColor : '#3CCC10',
                color: '#FFFFFF',
                marginTop: '33px',
              }}
              type='submit'
              children='Salvar nova senha'
              image={
                <img alt='switch-password-icon' src={switchPasswordIcon} />
              }
              loading={isLoading}
              disabled={isLoading}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default SwitchPassword;
